import classnames from 'classnames';
import isNil from 'lodash/isNil';
import { ReactNode } from 'react';
import ReactSelect, {
    components,
    type ClearIndicatorProps,
    type CSSObjectWithLabel,
    type DropdownIndicatorProps,
    type MenuListProps,
    type OptionProps,
    type SingleValueProps,
    type StylesConfig,
} from 'react-select';

import './Select_.scss';
import '@components//mixins/mixin.scss';
import mixins from '@components/mixins/mixin.module.scss';
import '@components/mixins/commonStyles.scss';
import '@components/Print/Print_.scss';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { isDefined } from '@utils/isDefined';

import { FieldTitle } from '@components/FieldTitle';
import { Icon, IconType } from '@components/Icon';
import { LeftTitle } from '@components/LeftTitle';
import { NotValidFieldText } from '@components/NotValidFieldText';
import { translate } from '@chaika/i18n/helper';
import { TranslationValues } from '@chaika/i18n/types';
import { normalizeYoFilter } from '@utils/normalizeYoString';
import { getMenuPortalProps } from './getMenuPortalProps';
import { useSelect } from './hooks/useSelect';
import type { DefaultOption } from './constants/constants';

export enum SelectHighlightColor {
    // @ts-ignore
    Red = mixins.Red,
    // @ts-ignore
    Red02 = mixins.Red02,
    // @ts-ignore
    Green = mixins.Green,
    // @ts-ignore
    LightGreen = mixins.LightGreen,
    // @ts-ignore
    Blue = mixins.Blue,
    // @ts-ignore
    BlackRedesign = mixins.BlackRedesign,
    // @ts-ignore
    Orange03 = mixins.Orange03,
}

export interface SelectHighlightConditionParams {
    values: any[];
    color: SelectHighlightColor;
}

// https://github.com/JedWatson/react-select#props
export interface SelectProps {
    options: OptionType[];
    dataTest?: string;
    leftTitle?: boolean;
    modifiers?: SelectModifiers[];
    title?: string;
    menuIsOpen?: boolean;
    smallMenuWidth?: boolean;
    widthByContent?: boolean;
    boldTitle?: boolean;
    value?: Nullable<OptionType>;
    name?: string;
    placeholder?: string;
    disabled?: boolean;
    locked?: boolean;
    isSearchable?: boolean;
    isClearable?: boolean;
    isValid?: boolean;
    isRequired?: boolean;
    notValidText?: string;
    hideSelectedOptions?: boolean;
    highlightConditionParams?: SelectHighlightConditionParams;
    menuPortalTarget?: HTMLElement | null;
    withDefaultValue?: boolean;
    defaultOptionType?: DefaultOption | TranslationValues;
    selectedValue?: Nullable<string | number>;
    forcePlaceholder?: boolean;
    resetValueOnUpdateOption?: boolean;
    onChange(params: Nullable<OptionType>, name?: string): void;
    onMenuOpen?(): void;
}

type Value = string | number | null | undefined;

export type OptionType<V = Value, D = object, L = string> = D & {
    value: V;
    label: L;
    isEhrBlocked?: boolean;
    isDisabled?: boolean;
    subGroup?: boolean;
    linkOption?: boolean;
    highlightColor?: SelectHighlightColor;
    highlightText?: string;
    additionalText?: string;
    customElement?: ReactNode;
    // [key: string]: any;
};

export type OptionTypeWithAdditionalData<D, V = Value> = OptionType<V> & D;

export enum SelectModifiers {
    // @ts-ignore
    Small = 'small',
    // @ts-ignore
    InheritWidth = 'inheritWidth',
}

/* eslint-disable */
export function Select({
    dataTest,
    leftTitle,
    options,
    title,
    value = null,
    name,
    disabled,
    locked,
    placeholder,
    isSearchable = true,
    isClearable = false,
    hideSelectedOptions = true,
    menuIsOpen,
    smallMenuWidth,
    menuPortalTarget,
    isValid,
    isRequired = false,
    notValidText,
    highlightConditionParams,
    onMenuOpen,
    onChange,
    modifiers = [],
    withDefaultValue,
    defaultOptionType,
    selectedValue,
    forcePlaceholder,
    resetValueOnUpdateOption,
}: SelectProps) {
    const {
        options: resolvedOptions,
        value: resolvedValue,
        placeholder: resolvedPlaceholder,
        handleChange,
        handleFocus,
        handleBlur,
    } = useSelect({
        options,
        value,
        selectedValue,
        withDefaultValue,
        defaultOptionType,
        forcePlaceholder,
        placeholder,
        title,
        name,
        resetValueOnUpdateOption,
        onChange,
    });

    const withValidStatus = !isNil(isValid);

    const select = (
        <div
            data-test={dataTest}
            data-testid={dataTest}
            className={classnames([
                'component_select__root',
                'component_select__root_alignedValue',
                'font_body',
                smallMenuWidth && 'component_select__root_smallMenuWidth',
                locked && 'component_select__root_locked',
                ...modifiers,
            ])}
        >
            {title && (
                <div className={classnames(['title'])}>
                    {!leftTitle && <FieldTitle title={isRequired ? `${title} *` : title} />}
                </div>
            )}
            <ReactSelect
                menuIsOpen={menuIsOpen}
                placeholder={resolvedPlaceholder ?? '-'}
                options={resolvedOptions}
                value={resolvedValue}
                filterOption={normalizeYoFilter}
                onChange={handleChange}
                hideSelectedOptions={hideSelectedOptions}
                isSearchable={isSearchable}
                isClearable={isClearable}
                menuPlacement={'auto'}
                isDisabled={disabled || locked}
                locked={locked}
                noOptionsMessage={noOptionsMessage}
                className={classnames(['component_select__root', 'hide_on_print'])}
                classNamePrefix={'react-select'}
                components={{ DropdownIndicator, MenuList, Option, SingleValue, ClearIndicator }}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onMenuOpen={onMenuOpen}
                {...getMenuPortalProps(menuPortalTarget, undefined)}
                {...({ locked } as any)}
                styles={{
                    option: getOptionStyles(highlightConditionParams),
                }}
            />
            <div className={classnames(['for_print', 'print_label'])}>{resolvedValue?.label ?? ''}</div>
            {withValidStatus && !isValid && notValidText && <NotValidFieldText title={notValidText} />}
        </div>
    );

    return leftTitle && title ? <LeftTitle title={title}>{select}</LeftTitle> : select;
}

export const DropdownIndicator = (props: DropdownIndicatorProps<OptionType, boolean>) => {
    const selectProps = props.selectProps as unknown as SelectProps & { menuIsOpen?: boolean };

    return (
        <components.DropdownIndicator {...props}>
            {selectProps.locked ? (
                <div />
            ) : (
                <div
                    className={classnames([
                        'component_select__openIconRedesign',
                        selectProps.menuIsOpen && 'component_select__openIconRedesign_revert',
                    ])}
                >
                    <Icon type={IconType.CHEVRON_DOWN} />
                </div>
            )}
        </components.DropdownIndicator>
    );
};

export const ClearIndicator = (props: ClearIndicatorProps<OptionType, boolean>) => {
    return (
        <components.ClearIndicator {...props}>
            <ClearIcon />
        </components.ClearIndicator>
    );
};

export const ClearIcon = () => {
    return (
        <div className={'component_select__icon_wrapper'}>
            <Icon type={IconType.CROSS_IN_CIRCLE} />
        </div>
    );
};

export const Option = (props: OptionProps<OptionType, boolean>) => {
    const { children, ...optionProps } = props;
    const isNew = (props.data as any).__isNew__;
    const options = props.options as readonly OptionType[];
    const hasOtherOptions = isNew && options?.some(o => !(o as any).__isNew__);

    return (
        <div
            className={classnames([
                props.data.subGroup && 'component_select__subOption',
                props.data.linkOption && 'component_select__link_option',
                props.data.linkOption && 'component_select__link_option',
                props.data.isDisabled && 'component_select__option_disabled',
                props.data.isEhrBlocked && 'component_select__option_blocked',
                hasOtherOptions && 'component_select__option_border_top',
            ])}
        >
            <components.Option
                {...optionProps}
                children={OptionChildren(
                    children,
                    props.data.highlightColor,
                    props.data.highlightText,
                    props.data.additionalText,
                    props.data.customElement,
                )}
            />
        </div>
    );
};

const OptionChildren = (
    children: any,
    highlightColor?: SelectHighlightColor,
    highlightText?: string,
    additionalText?: string,
    customElement?: ReactNode,
) => {
    const withHighlightText = isDefined(highlightText);
    const withAdditionalText = isDefined(additionalText);

    return withHighlightText || withAdditionalText ? (
        <div className={'optionChildrenWrapper'}>
            {children}
            <div
                className={'optionChildren_highlightText'}
                style={{ color: highlightColor ? `${highlightColor}` : undefined }}
            >
                {highlightText ?? ''}
            </div>
            {withAdditionalText && <div className={'additionalText'}>{additionalText}</div>}
        </div>
    ) : customElement ? (
        customElement
    ) : (
        <>{children}</>
    );
};

export const SingleValue = (props: SingleValueProps<OptionType, boolean>) => {
    const options = props.options as readonly OptionType[];
    const data = options.find(it => it.value === props.data.value);
    return (
        <div className={classnames([data?.isEhrBlocked && 'component_select__option_blocked'])}>
            <components.SingleValue {...props} />
            {isDefined(data?.highlightColor) && (
                <div
                    className={'highlightIndicator'}
                    style={{ backgroundColor: `${data?.highlightColor}` }}
                />
            )}
        </div>
    );
};

export const MenuList = (props: MenuListProps<OptionType, boolean>) => {
    const selectProps = props.selectProps as unknown as SelectProps & {
        menuStyle?: Record<string, string>;
    };
    const { menuStyle = {} } = selectProps;

    return (
        <components.MenuList {...props}>
            <div style={menuStyle}>{props.children}</div>
        </components.MenuList>
    );
};

export function noOptionsMessage({ inputValue }: { inputValue: string }): string {
    return inputValue === '' ? translate('inputValue') : translate('nothingFound');
}

export const getOptionStyles = (
    highlightConditionParams?: SelectHighlightConditionParams,
): NonNullable<StylesConfig<OptionType, false>['option']> => {
    return (provided: CSSObjectWithLabel, state: OptionProps<OptionType, false>) => {
        const needToHighlight = highlightConditionParams?.values.includes(state.data.value);

        return {
            ...provided,
            backgroundColor:
                needToHighlight && highlightConditionParams?.color
                    ? `${highlightConditionParams.color} !important`
                    : undefined,
        };
    };
};
